import api, { setAccessToken, setRefreshToken, clearTokens } from "./api";
import type { User, LoginRequest, RegisterRequest, TokenResponse } from "@/types/user";
import type { MenuItem } from "@/types/admin";
import { getOrCreateDeviceId, ensureDeviceId, setDeviceIdFromServer } from "@/utils/deviceId";

// WebView 환경 감지 헬퍼 함수
const getDeviceType = (): "web" | "app" => {
  return typeof window !== "undefined" && (window as any).isReactNativeWebView
    ? "app"
    : "web";
};

/** 이 계정으로 모바일 앱이 설치·사용 중인지 (계정 단위 — 휴대폰 IP 는 수시로 바뀌어 기기 판정 불가) */
export interface MobileAppStatus {
  /** 최근 14일 안에 쓴 앱 신뢰 기기 또는 활성 앱 푸시 토큰이 있음 */
  installed: boolean;
  platform: "android" | "ios" | null;
  version: string | null;
  last_used_at: string | null;
  /** 관리자가 메신저 설치를 강제함 — Android 브라우저는 앱이 깔려 있으면 헤더 메신저 아이콘을 숨긴다 */
  force_install?: boolean;
}

export interface DesktopAppStatus {
  /** 데스크톱 앱이 지금 이 PC(같은 접속 IP)에서 서버에 붙어 있음 */
  running_here: boolean;
  /** 같은 IP 에서 최근 30분 안에 쓴 데스크톱 기기가 있음 (서버 DESKTOP_APP_INSTALLED_WINDOW_MINUTES) */
  installed_here: boolean;
  /** IP 무관하게 내 계정으로 최근 30분 안에 활동한 데스크톱 기기가 있음 */
  installed_anywhere: boolean;
  version: string | null;
  last_used_at: string | null;
  client_ip: string | null;
  /** 관리자가 이 사용자에게 데스크톱 메신저 설치를 강제함 — Windows 브라우저에서 메신저 아이콘 클릭 시 웹 채팅 대신 설치 안내 */
  force_install?: boolean;
}

export const authService = {
  async login(data: LoginRequest): Promise<TokenResponse> {
    // device_type 자동 설정 + device_id 포함
    // ensureDeviceId(): localStorage/IndexedDB 중 살아있는 값을 우선 복구
    const loginData = {
      ...data,
      device_type: data.device_type || getDeviceType(),
      device_id: data.device_id || (await ensureDeviceId()),
    };
    const response = await api.post<TokenResponse>("/auth/login", loginData);
    // 서버가 확정한 device_id(쿠키 복구 결과 포함)를 클라이언트 저장소에 다시 동기화
    setDeviceIdFromServer(response.data.device_id);
    // 토큰을 즉시 저장하지 않고 반환만 함 (authStore에서 모든 검증 후 저장)
    return response.data;
  },

  /**
   * 메신저 앱이 발급한 1회용 코드를 브라우저 세션으로 교환 (/auth/handoff 페이지)
   * 응답은 로그인과 같은 TokenResponse — 토큰 저장은 호출자가 한다
   */
  async exchangeDesktopHandoff(code: string): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>("/auth/handoff/exchange", {
      code,
      device_id: await ensureDeviceId(),
    });
    setDeviceIdFromServer(response.data.device_id);
    return response.data;
  },

  async register(data: RegisterRequest): Promise<User> {
    // Register the user with company_code as query parameter
    const { company_code, ...registerData } = data;
    const url = company_code
      ? `/auth/register?company_code=${encodeURIComponent(company_code)}`
      : "/auth/register";
    const registerResponse = await api.post<User>(url, registerData);

    // 회원가입 후 자동 로그인 시도 (승인된 경우만 성공)
    // 승인 대기 중인 경우 로그인 실패는 정상이므로 에러 무시
    try {
      const loginResponse = await api.post<TokenResponse>("/auth/login", {
        email: data.email,
        password: data.password,
        device_type: getDeviceType(),
        device_id: getOrCreateDeviceId(),
      });
      setDeviceIdFromServer(loginResponse.data.device_id);
      const { access_token, refresh_token } = loginResponse.data;
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
    } catch (error) {
      // 승인 대기 중인 사용자는 로그인 실패 - 정상적인 흐름
      console.log("회원가입 완료, 관리자 승인 대기 중");
    }

    return registerResponse.data;
  },

  async logout(): Promise<void> {
    try {
      // device_type을 쿼리 파라미터로 전달
      const deviceType = getDeviceType();
      await api.post(`/auth/logout?device_type=${deviceType}`);
    } finally {
      clearTokens();
    }
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>("/users/me");
    return response.data;
  },

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await api.put<User>("/auth/me/profile", data);
    return response.data;
  },

  async uploadProfileImage(file: File): Promise<User> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<User>("/auth/me/profile-image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  async deleteProfileImage(): Promise<User> {
    const response = await api.delete<User>("/auth/me/profile-image");
    return response.data;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ access_token: string; refresh_token: string }> {
    const response = await api.put("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post("/auth/forgot-password", { email });
  },

  async confirmReset(token: string): Promise<{ temporary_password: string; message: string }> {
    const response = await api.post<{ temporary_password: string; message: string }>("/auth/confirm-reset", { token });
    return response.data;
  },

  async getMyMenus(): Promise<MenuItem[]> {
    const response = await api.get<MenuItem[]>("/auth/me/menus");
    return response.data;
  },

  /**
   * 접근 인가된 메뉴 경로 목록 (라우트 가드용)
   * 사이드바 트리(/me/menus)와 달리 인가 기준이며 삭제된 메뉴는 제외된다.
   */
  async getMyAccessiblePaths(): Promise<string[]> {
    const response = await api.get<{ paths: string[] }>("/auth/me/accessible-paths");
    return response.data.paths;
  },

  async getMyCompanies(): Promise<{
    companies: Array<{
      company_id: string;
      company_name: string;
      company_code: string;
      logo_url: string | null;
      is_current: boolean;
    }>;
    total: number;
  }> {
    const response = await api.get("/auth/my-companies");
    return response.data;
  },

  /** 이 PC 에 Windows 메신저가 실행 중/설치되어 있는지 (헤더 "Windows 메신저 설치" 항목 숨김·설치 페이지 안내용) */
  async getDesktopAppStatus(): Promise<DesktopAppStatus> {
    const response = await api.get<DesktopAppStatus>("/auth/me/desktop-app");
    return response.data;
  },

  async getMobileAppStatus(): Promise<MobileAppStatus> {
    const response = await api.get<MobileAppStatus>("/auth/me/mobile-app");
    return response.data;
  },

  async switchCompany(companyCode: string): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>("/auth/switch-company", null, {
      params: { company_code: companyCode },
    });
    return response.data;
  },
};

export default authService;
